import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Drawer, List, SvgIconProps, Toolbar } from "@mui/material";
import { ParseKeys } from "i18next";
import type { JSX } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import "./translations";

import {
  HomeIcon,
  OfficeBuildingIcon,
  OneClickIcon,
  OutlierShield,
  PersonIcon,
  RealEstatePriceIndexIcon,
} from "@/assets";
import { usePermissions } from "@/hooks/permissionsHooks/usePermissions";
import { ModuleType } from "@/types/ModuleType";

import { SidebarItem } from "./SidebarItem";

type SidebarItemType = {
  selected: boolean;
  icon: (props: SvgIconProps) => JSX.Element;
  label: ParseKeys<"Sidebar"> | TemplateStringsArray;
  onClick?: () => void;
  testLabel: string;
  disabled?: boolean | undefined;
};

export const Sidebar = (): JSX.Element => {
  const { t } = useTranslation("Sidebar");
  const navigate = useNavigate();
  const location = useLocation();
  const userPermissions = usePermissions();

  const items: SidebarItemType[] = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/$/, "");
    switch (normalizedPath) {
      case "/backoffice":
      case "/backoffice/user":
      case "/backoffice/institution":
      case "/backoffice/subscription":
      case "/backoffice/settings":
        return [
          {
            icon: OfficeBuildingIcon,
            label: "sidebarItems.institution",
            selected: normalizedPath === "/backoffice/institution" || normalizedPath === "/backoffice",
            onClick: () => navigate("/backoffice/institution"),
            testLabel: "side-menu-institution",
          },
          {
            icon: PersonIcon,
            label: "sidebarItems.user",
            selected: normalizedPath === "/backoffice/user",
            onClick: () => navigate("/backoffice/user"),
            testLabel: "side-menu-user",
          },
          {
            icon: RealEstatePriceIndexIcon,
            label: "sidebarItems.indices",
            selected: normalizedPath === "/backoffice/subscription",
            onClick: () => navigate("/backoffice/subscription"),
            testLabel: "side-menu-indices",
          },
          {
            icon: (props: SvgIconProps) => <SettingsIcon {...props} />,
            label: "sidebarItems.settings",
            selected: normalizedPath === "/backoffice/settings",
            onClick: () => navigate("/backoffice/settings"),
            testLabel: "side-menu-settings",
          },
        ];
      default:
        return [
          {
            icon: HomeIcon,
            label: "sidebarItems.home",
            selected: normalizedPath === "/",
            testLabel: "side-menu-home",
            disabled: true,
          },
          {
            icon: RealEstatePriceIndexIcon,
            label: "sidebarItems.realEstatePriceIndex",
            selected:
              normalizedPath === "/indexes" ||
              normalizedPath === "/playground" ||
              normalizedPath === "/portfolio-analysis",
            onClick: (): void => {
              navigate("/indexes");
            },
            testLabel: "side-menu-index-proftolio",
          },
          {
            icon: OneClickIcon,
            label: "sidebarItems.oneClick",
            selected: normalizedPath === "/oneclick",
            onClick: (): void => {
              navigate("/oneclick");
            },
            testLabel: "side-menu-one-click",
            disabled: !userPermissions.has(ModuleType.ONECLICK),
          },
          {
            icon: OutlierShield,
            label: "sidebarItems.outlierShield",
            selected: normalizedPath === "/outlier-shield",
            onClick: (): void => {
              navigate("/outlier-shield");
            },
            testLabel: "side-menu-outlier-shield",
            disabled: !userPermissions.has(ModuleType.OUTLIERSHIELD),
          },
        ];
    }
  }, [navigate, location, userPermissions]);

  return (
    <Drawer
      variant="permanent"
      sx={(theme) => ({
        width: theme.sidebar.drawerWidth,
        flexShrink: 0,

        [`& .MuiDrawer-paper`]: { width: theme.sidebar.drawerWidth, boxSizing: "border-box" },
      })}
      PaperProps={{
        sx: (theme) => ({
          backgroundColor: theme.sidebar.background,
          width: theme.sidebar.drawerWidth,
          border: 0,
        }),
      }}
    >
      <Toolbar />
      <Box sx={{ width: "100%" }}>
        <List sx={{ width: "100%" }} disablePadding>
          {items
            .filter((item) => {
              if (item.label === "sidebarItems.outlierShield") {
                return userPermissions.has(ModuleType.OUTLIERSHIELD);
              }
              return true;
            })
            .map(({ icon, label, selected, onClick, testLabel, disabled }, index) => (
              <SidebarItem
                key={index}
                icon={icon}
                label={t(label)}
                selected={selected}
                onClick={onClick}
                testLabel={testLabel}
                disabled={disabled}
              />
            ))}
        </List>
      </Box>
    </Drawer>
  );
};
